import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ToastProvider } from '../components/Toast'
import useBodyScrollLock from '../components/useBodyScrollLock'
import useHashPart from '../components/useHashPart'
import ConsoleLogPanel from './ConsoleLogPanel'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import { isEnglish } from '../lib/i18n'
import { useSession } from '../lib/sessionContext'
import { shell } from './strings'
import './AppLayout.css'

/** Below this the rail becomes an off-canvas drawer; matches the CSS breakpoint. */
const NARROW = '(max-width: 1099px)'

/**
 * Frame for every admin page — the React equivalent of _AdminsBaseLayout.cshtml:
 * fixed rail, sticky header, page content, then the footer carrying the website
 * footer text and the build version.
 */
export default function AppLayout() {
  /* Live, from GET /api/session — so the rail, the account menu and the footer show
     the real roles, license feature sets and settings rather than a stand-in. */
  const { session, buildVersion } = useSession()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [consoleOpen, setConsoleOpen] = useState(false)
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches)
  const { pathname } = useLocation()

  useEffect(() => {
    const query = window.matchMedia(NARROW)
    const onChange = (event) => setNarrow(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  // The rail is a drawer below 1100px; navigating should close it.
  useEffect(() => setSidebarOpen(false), [pathname])

  useEffect(() => {
    if (!sidebarOpen) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSidebarOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [sidebarOpen])

  useBodyScrollLock(sidebarOpen)

  /* A destination the command search pointed INTO a page — scrolls to whatever
     declares the hash and flashes it. Section tabs are handled by the pages
     themselves (useHashTab); this is for blocks further down. */
  useHashPart()

  return (
    <ToastProvider>
      <div className="app">
      {/* The rail holds destinations and nothing else. */}
      <Sidebar
        session={session}
        open={sidebarOpen}
        narrow={narrow}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="app__main">
        <Topbar
          session={session}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((open) => !open)}
          onShowConsoleLogs={() => setConsoleOpen(true)}
        />

        <main className="app__content">
          <div className="app__container">
            <Outlet />
          </div>
        </main>

        {/* Who the panel belongs to and which build is running. It is reference
            information about the installation, not an account setting, so it
            reads at the foot of the page — where a footer line is looked for —
            rather than hidden at the bottom of the account menu. */}
        <footer className="app__footer">
          <div className="app__footer-inner">
            <span className="app__footer-text">
              {/* The layout's own rule: the English footer row when the panel is in
                  English, the Arabic one otherwise — and the Arabic one as the fallback,
                  because only it is guaranteed to be filled in. */}
              <bdi>
                {(isEnglish && session.settings.WebsiteFooterTextEn) ||
                  session.settings.WebsiteFooterText}
              </bdi>
            </span>
            {/* The release, not the commit: `1.0.0+<40-char hash>` printed in full was
                debug output in the product's chrome. The whole build string stays one
                hover away for whoever is filing a report. */}
            <span className="app__footer-version" title={buildVersion}>
              <bdi>{shell.version}</bdi> <span dir="ltr">{String(buildVersion ?? '').split('+')[0]}</span>
            </span>
          </div>
        </footer>
      </div>

      <ConsoleLogPanel open={consoleOpen} onOpenChange={setConsoleOpen} />
      </div>
    </ToastProvider>
  )
}
